import { Controller } from "@hotwired/stimulus"
import ahoy from "ahoy.js"

// "Are you still there?" for study participants (study-plan/idle-modal-brief.md).
// After two minutes without activity, or when the tab is hidden, a modal covers the
// page until the participant says they're back. A playing video (video_controller.js)
// counts as activity. While the page is visible and the modal is closed, a heartbeat
// goes to Ahoy every 30 seconds; Negotiage::ActiveTime works out
// active time from these events on the server. Each page load (Turbo navigations
// included) is its own page_id, so a second tab's events are kept apart.
export default class extends Controller {
  static targets = ["dialog"]
  static values = { idleAfter: { type: Number, default: 120_000 }, heartbeatEvery: { type: Number, default: 30_000 } }

  connect() {
    this.pageId = crypto.randomUUID()
    // Turbo changes the URL before the old page disconnects, so read it now; otherwise
    // the heartbeat sent from disconnect() would be filed under the next page.
    this.path = window.location.pathname
    this.videosPlaying = new Set()
    // A page Turbo restores from its cache can come back with the modal open.
    this.dialogTarget.close()
    if (document.hidden) {
      this.open()
    } else {
      this.resume()
    }
  }

  disconnect() {
    // Leaving the page (a Turbo visit or IAGO) ends this page's active time now, not
    // at its last heartbeat.
    if (this.active) this.track("Heartbeat")
    this.stop()
    this.dialogTarget.close()
  }

  // Mouse, keys, scrolling and touch on the page.
  activity() {
    this.lastActivity = Date.now()
  }

  // A playing video holds off the modal; the 2 minutes start again when it stops.
  videoPlay(event) {
    this.videosPlaying.add(event.target)
  }

  videoStop(event) {
    this.videosPlaying.delete(event.target)
    this.activity()
  }

  visibility() {
    if (document.hidden) {
      this.track("Page hidden")
      if (this.active) {
        this.stop()
        this.open()
      }
    } else {
      this.track("Page visible")
    }
  }

  // "Continue Reviewing", or any key while the modal is showing.
  dismiss(event) {
    if (!this.dialogTarget.open) return
    event?.preventDefault()
    this.dialogTarget.close()
    this.track("Idle modal dismissed")
    this.resume()
  }

  // private

  resume() {
    this.stop()
    this.active = true
    this.lastActivity = Date.now()
    this.track("Heartbeat")
    this.heartbeat = setInterval(() => this.track("Heartbeat"), this.heartbeatEveryValue)
    this.armIdleCheck(this.idleAfterValue)
  }

  stop() {
    this.active = false
    clearInterval(this.heartbeat)
    clearTimeout(this.idleCheck)
  }

  armIdleCheck(delay) {
    this.idleCheck = setTimeout(() => {
      if (this.videosPlaying.size > 0) this.activity()
      const remaining = this.idleAfterValue - (Date.now() - this.lastActivity)
      if (remaining > 0) {
        this.armIdleCheck(remaining)
      } else {
        this.track("Idle modal shown")
        this.stop()
        this.open()
      }
    }, delay)
  }

  open() {
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
  }

  track(name) {
    ahoy.track(name, { page_id: this.pageId, page: this.path })
  }
}
